"use client";
import { handleCheckoutSession } from "@/app/utils/checkout-session";
import { scrollToHash } from "@/app/utils/scroll";
import { DISCOUNT_ID, PRICING_ID_T1, PRICING_NAME_T1 } from "@/lib/constants";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";

export default function MobileMenu() {
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  // Username/Display name variable
  const [uid, setUID] = useState("");

  const trigger = useRef<HTMLButtonElement>(null);
  const mobileNav = useRef<HTMLDivElement>(null);

  // Load username
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedData = localStorage.getItem("uid");
      if (storedData) {
        setUID(storedData);
      }
    }
  }, []);

  // close the mobile menu on click outside
  useEffect(() => {
    const clickHandler = ({ target }: { target: EventTarget | null }): void => {
      if (!mobileNav.current || !trigger.current) return;
      if (
        !mobileNavOpen ||
        mobileNav.current.contains(target as Node) ||
        trigger.current.contains(target as Node)
      )
        return;
      setMobileNavOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close the mobile menu if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: { keyCode: number }): void => {
      if (!mobileNavOpen || keyCode !== 27) return;
      setMobileNavOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  // Push the hash onto the URL and scroll
  const handleClick = async (hash) => {
    if (window.location.pathname != "/") {
      window.location.href = `/#${hash}`;
      return;
    }
    history.replaceState(null, "", `/#${hash}`);
    scrollToHash(hash);
  };

  // Sign out
  const handleSignOut = async () => {
    localStorage.removeItem("uid");
    window.location.href = "/";
    toast.success("You have signed out.");
  };

  return (
    <div className="lg:hidden">
      {/* Hamburger button */}
      <button
        ref={trigger}
        className={`hamburger ${mobileNavOpen && "active"}`}
        aria-controls="mobile-nav"
        aria-expanded={mobileNavOpen}
        onClick={() => setMobileNavOpen(!mobileNavOpen)}
      >
        <span className="sr-only">Menu</span>
        <svg
          className="w-6 h-6 fill-current text-zinc-300 hover:text-white transition duration-150 ease-in-out"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect y="4" width="24" height="2" rx="1" />
          <rect y="11" width="24" height="2" rx="1" />
          <rect y="18" width="24" height="2" rx="1" />
        </svg>
      </button>
      {/* Mobile navigation */}
      <nav
        id="mobile-nav"
        ref={mobileNav}
        className="absolute top-full z-20 left-0 w-full px-4 sm:px-6 overflow-hidden transition-all duration-300 ease-in-out"
        style={
          mobileNavOpen
            ? { maxHeight: mobileNav.current?.scrollHeight, opacity: 1 }
            : { maxHeight: 0, opacity: 0.8 }
        }
      >
        <div className="bg-panel border border-line rounded-2xl shadow-2xl shadow-black/40 px-10 py-10 flex flex-col gap-y-8">
          <div className="grid grid-cols-3 gap-6">
            <ul className="flex flex-col justify-start items-baseline gap-y-2">
              <li className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1">Product</li>
              <li>
                <button
                  onClick={() => {
                    handleClick("features");
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Features
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    handleClick("pricing");
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Pricing
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = "https://docs.extensionfast.com";
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Docs
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    handleClick("faq");
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  FAQ
                </button>
              </li>
            </ul>
            <ul className="flex flex-col justify-start items-baseline gap-y-2">
              <li className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1">Company</li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = "/blog";
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Blog
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = "mailto:mmcgarvey@extensionfast.com";
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Contact
                </button>
              </li>
            </ul>
            <ul className="flex flex-col justify-start items-baseline gap-y-2">
              <li className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1">Legal</li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = "/privacy";
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = "/terms";
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Terms of Service
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = "/signin";
                    setMobileNavOpen(false);
                  }}
                  className="font-medium w-full text-zinc-300 hover:text-white text-left"
                >
                  Sign In
                </button>
              </li>
            </ul>
          </div>
          <button
            className={`btn btn-ghost text-white bg-primary hover:bg-secondary px-6 py-2 rounded-full ring-1 ring-inset ring-white/15 text-lg`}
            onClick={() =>
              handleCheckoutSession(
                PRICING_ID_T1,
                DISCOUNT_ID,
                PRICING_NAME_T1,
                "mobile_menu",
              )
            }
          >
            Get ExtensionFast
          </button>
        </div>
      </nav>
    </div>
  );
}
